import { GetTeamResponse } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import {
    getFallbackErrorKey,
    getImageRejectionCode,
    isApiError
} from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation, useTeamPermissions } from '../hooks';
import { addOrUpdateAllTeamsCache } from '../queries/company-cache';

type UseUploadTeamImageActionProps = {
    teamId: string;
};

type UseUploadTeamImageActionData = {
    /** Whether this viewer can change this team's photo right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    uploadImage: (file: File) => Promise<GetTeamResponse>;
    loading: boolean;
    error: string;
};

/**
 * Replacing a team's photo: whether this viewer may do it, why not when they
 * may not, and the call itself. The file is rejected by the server rather than
 * the client, so the refusals it can give back are spelled out here.
 */
export function useUploadTeamImageAction(
    props: UseUploadTeamImageActionProps
): UseUploadTeamImageActionData {
    const { teamId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { canEditTeam } = useTeamPermissions({ teamId });

    const [isAllowed, reason] = useMemo(() => {
        if (!canEditTeam) {
            return [false, t('actions.upload_team_image.reason_not_permitted')];
        }

        return [true, ''];
    }, [canEditTeam, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/teams/{id}/image` declares gets a branch, plus the rejection
    // codes the endpoint sends but does not declare.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            switch (getImageRejectionCode(error)) {
                case 'UNSUPPORTED_FILE_FORMAT':
                    return t('shared.errors.unsupported_file_format');
                case 'UNSUPPORTED_FILE_SIZE':
                    return t('shared.errors.unsupported_file_size');
                case 'UNSUPPORTED_IMAGE_DIMENSIONS':
                    return t('shared.errors.unsupported_image_dimensions');
            }

            if (isApiError(error, 'CANNOT_PROCESS_FILE')) {
                return t('shared.errors.cannot_process_file');
            }
            // Both mean the same to the user: not a team they may change.
            if (
                isApiError(error, 'CANNOT_UPDATE_TEAM') ||
                isApiError(error, 'TEAM_NOT_FOUND')
            ) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (file: File) =>
            openApiRef.current.teams
                .uploadTeamImage(teamId, { file })
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllTeamsCache(queryClient, data.data.team);
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        uploadImage: (file: File): Promise<GetTeamResponse> =>
            mutation.mutateAsync(file),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
